import React, { useState, useEffect } from 'react';
import { Card } from 'primereact/card';
import { DataTable } from 'primereact/datatable';
import { Column } from 'primereact/column';
import { Chart } from 'primereact/chart';
import { Button } from 'primereact/button';
import { Tag } from 'primereact/tag';
import { ProgressBar } from 'primereact/progressbar';
import { TabView, TabPanel } from 'primereact/tabview';
import { Dropdown } from 'primereact/dropdown';
import { Calendar } from 'primereact/calendar';
import { useNavigate } from 'react-router-dom';
import './index.scss';

const ClaimsDashboard = () => {
  const navigate = useNavigate();
  const [dateRange, setDateRange] = useState([new Date(2025, 0, 1), new Date()]);
  const [selectedState, setSelectedState] = useState('all');
  const [activeIndex, setActiveIndex] = useState(0);

  // KPI Data
  const kpiData = {
    totalOpenClaims: 87,
    claimsOverdue: 12,
    todaysClaims: 8,
    highestClaimsCategory: 'Motor - Comprehensive',
    highestClaimsPercentage: 62,
    maxClaimsByState: 'Metro Manila',
    statePercentage: 37,
    avgProcessingTime: '4.2 days',
    totalClaimsValue: '₱12,450,000',
    approvalRate: '78%'
  };

  // Recent Claims Data
  const recentClaims = [
    {
      claimId: 'CLM2025001388',
      lob: 'Motor',
      customer: 'Juan Dela Cruz',
      policy: 'MIC-2025-MOT-00145',
      lossDate: '25/01/2025',
      reportedDate: '26/01/2025',
      reporter: 'Maria Santos',
      priority: 'high',
      status: 'IN PROGRESS',
      amount: '₱85,000'
    },
    {
      claimId: 'CLM2025001387',
      lob: 'Employee Benefits',
      customer: 'ABC Corporation',
      policy: 'AXA-2025-HLT-00891',
      lossDate: '24/01/2025',
      reportedDate: '24/01/2025',
      reporter: 'Pedro Garcia',
      priority: 'medium',
      status: 'IN PROGRESS',
      amount: '₱125,000'
    },
    {
      claimId: 'CLM2025001386',
      lob: 'Property',
      customer: 'Jose Rizal Corp',
      policy: 'MAL-2025-PRO-00234',
      lossDate: '23/01/2025',
      reportedDate: '24/01/2025',
      reporter: 'Carlos Mendoza',
      priority: 'low',
      status: 'IN PROGRESS',
      amount: '₱450,000'
    },
    {
      claimId: 'CLM2025001385',
      lob: 'Motor',
      customer: 'Sarah Lopez',
      policy: 'PIO-2025-MOT-00678',
      lossDate: '22/01/2025',
      reportedDate: '23/01/2025',
      reporter: 'Anna Lim',
      priority: 'low',
      status: 'UNDER REVIEW',
      amount: '₱65,000'
    },
    {
      claimId: 'CLM2025001384',
      lob: 'Travel',
      customer: 'Miguel Torres',
      policy: 'AIG-2025-TRV-00123',
      lossDate: '20/01/2025',
      reportedDate: '21/01/2025',
      reporter: 'Elena Cruz',
      priority: 'low',
      status: 'IN PROGRESS',
      amount: '₱35,000'
    },
    {
      claimId: 'CLM2025001383',
      lob: 'Health',
      customer: 'Benjamin Santos',
      policy: 'MED-2025-HLT-00456',
      lossDate: '18/01/2025',
      reportedDate: '19/01/2025',
      reporter: 'Rosa Fernandez',
      priority: 'low',
      status: 'APPROVED',
      amount: '₱250,000'
    },
    {
      claimId: 'CLM2025001382',
      lob: 'Motor',
      customer: 'Lisa Chen',
      policy: 'STD-2025-MOT-00789',
      lossDate: '17/01/2025',
      reportedDate: '17/01/2025',
      reporter: 'David Lim',
      priority: 'high',
      status: 'CLOSED',
      amount: '₱95,000'
    },
    {
      claimId: 'CLM2025001381',
      lob: 'Property',
      customer: 'ABC Trading Inc',
      policy: 'FPG-2025-PRO-00345',
      lossDate: '15/01/2025',
      reportedDate: '16/01/2025',
      reporter: 'Roberto Reyes',
      priority: 'medium',
      status: 'IN PROGRESS',
      amount: '₱750,000'
    }
  ];

  // Claims by State data for map
  const claimsByState = [
    { state: 'Metro Manila', value: 312, percentage: 37 },
    { state: 'Cebu', value: 178, percentage: 21 },
    { state: 'Davao', value: 145, percentage: 17 },
    { state: 'Laguna', value: 89, percentage: 11 },
    { state: 'Cavite', value: 67, percentage: 8 },
    { state: 'Others', value: 51, percentage: 6 }
  ];

  // Claims by Source Chart Data
  const claimsBySourceData = {
    labels: ['Self-Service Portal', 'Call Center', 'Agent/Broker', 'Mobile App', 'Email', 'Walk-in'],
    datasets: [{
      data: [35, 28, 20, 10, 5, 2],
      backgroundColor: [
        '#0066CC',
        '#4285F4',
        '#7BAAF7',
        '#FFA500',
        '#FFD700',
        '#90EE90'
      ]
    }]
  };

  // Claims Trend Chart Data
  const claimsTrendData = {
    labels: ['Jan', 'Feb', 'Mar', 'Apr', 'May', 'Jun', 'Jul', 'Aug', 'Sep', 'Oct', 'Nov', 'Dec'],
    datasets: [
      {
        label: 'Claims Submitted',
        data: [165, 159, 180, 181, 156, 195, 240, 218, 245, 234, 256, 278],
        borderColor: '#0066CC',
        backgroundColor: 'rgba(0, 102, 204, 0.1)',
        tension: 0.4
      },
      {
        label: 'Claims Approved',
        data: [128, 138, 145, 162, 140, 175, 198, 185, 210, 198, 220, 235],
        borderColor: '#00C851',
        backgroundColor: 'rgba(0, 200, 81, 0.1)',
        tension: 0.4
      },
      {
        label: 'Claims Rejected',
        data: [37, 21, 35, 19, 16, 20, 42, 33, 35, 36, 36, 43],
        borderColor: '#FF4444',
        backgroundColor: 'rgba(255, 68, 68, 0.1)',
        tension: 0.4
      }
    ]
  };

  // Loss Ratio by Product
  const lossRatioData = {
    labels: ['Motor', 'Health', 'Employee Benefits', 'Property', 'Travel', 'Marine', 'Life'],
    datasets: [{
      label: 'Loss Ratio %',
      data: [68, 72, 35, 45, 38, 52, 41],
      backgroundColor: [
        '#FF6384',
        '#36A2EB',
        '#4CAF50',
        '#FFCE56',
        '#4BC0C0',
        '#9966FF',
        '#FF9F40'
      ]
    }]
  };

  const statusBodyTemplate = (rowData) => {
    const getSeverity = (status) => {
      switch (status) {
        case 'CLOSED': return 'success';
        case 'APPROVED': return 'info';
        case 'IN PROGRESS': return 'warning';
        case 'UNDER REVIEW': return 'danger';
        default: return null;
      }
    };
    return <Tag value={rowData.status} severity={getSeverity(rowData.status)} />;
  };

  const priorityBodyTemplate = (rowData) => {
    const getPriorityIcon = (priority) => {
      switch (priority) {
        case 'high': return '🔴';
        case 'medium': return '🟡';
        case 'low': return '🟢';
        default: return '';
      }
    };
    return <span>{getPriorityIcon(rowData.priority)}</span>;
  };

  const actionBodyTemplate = (rowData) => {
    return (
      <div className="flex gap-2">
        <Button icon="pi pi-eye" rounded text severity="info" onClick={() => navigate(`/claims/view/${rowData.claimId}`)} />
        <Button icon="pi pi-pencil" rounded text onClick={() => navigate(`/claims/edit/${rowData.claimId}`)} />
      </div>
    );
  };

  return (
    <div className="claims-dashboard">
      <div className="dashboard-header">
        <div className="header-content">
          <h2>Claims Management Dashboard</h2>
          <div className="header-actions">
            <Calendar value={dateRange} onChange={(e) => setDateRange(e.value)} selectionMode="range" placeholder="Select Date Range" />
            <Button label="Export Report" icon="pi pi-download" severity="info" />
            <Button label="New Claim" icon="pi pi-plus" onClick={() => navigate('/claims/new')} />
          </div>
        </div>
      </div>

      {/* KPI Cards Row */}
      <div className="kpi-row">
        <Card className="kpi-card">
          <div className="kpi-content">
            <i className="pi pi-folder-open kpi-icon" style={{ color: '#0066CC' }}></i>
            <div className="kpi-details">
              <span className="kpi-label">Total Open Claims</span>
              <span className="kpi-value">{kpiData.totalOpenClaims}</span>
            </div>
          </div>
        </Card>

        <Card className="kpi-card">
          <div className="kpi-content">
            <i className="pi pi-exclamation-triangle kpi-icon" style={{ color: '#FF6B6B' }}></i>
            <div className="kpi-details">
              <span className="kpi-label">Claims Overdue</span>
              <span className="kpi-value">{kpiData.claimsOverdue}</span>
            </div>
          </div>
        </Card>

        <Card className="kpi-card">
          <div className="kpi-content">
            <i className="pi pi-calendar kpi-icon" style={{ color: '#4ECDC4' }}></i>
            <div className="kpi-details">
              <span className="kpi-label">Today's Claims</span>
              <span className="kpi-value">{kpiData.todaysClaims}</span>
            </div>
          </div>
        </Card>

        <Card className="kpi-card">
          <div className="kpi-content">
            <i className="pi pi-chart-line kpi-icon" style={{ color: '#95E77E' }}></i>
            <div className="kpi-details">
              <span className="kpi-label">Highest Claims</span>
              <span className="kpi-value">{kpiData.highestClaimsCategory}</span>
              <span className="kpi-percentage">{kpiData.highestClaimsPercentage}%</span>
            </div>
          </div>
        </Card>

        <Card className="kpi-card">
          <div className="kpi-content">
            <i className="pi pi-map-marker kpi-icon" style={{ color: '#FFD93D' }}></i>
            <div className="kpi-details">
              <span className="kpi-label">Max Claims By State</span>
              <span className="kpi-value">{kpiData.maxClaimsByState}</span>
              <span className="kpi-percentage">{kpiData.statePercentage}%</span>
            </div>
          </div>
        </Card>
      </div>

      {/* Main Content Area */}
      <div className="dashboard-content">
        <div className="content-left">
          {/* Recent Claims Table */}
          <Card title="Recent Claims" className="recent-claims-card">
            <DataTable
              value={recentClaims}
              paginator
              rows={10}
              rowsPerPageOptions={[5, 10, 25]}
              className="claims-table"
            >
              <Column field="claimId" header="CLAIM#" />
              <Column field="lob" header="LOB" />
              <Column field="customer" header="CUSTOMER" />
              <Column field="policy" header="POLICY" />
              <Column field="lossDate" header="LOSS DATE" />
              <Column field="reportedDate" header="REPORTED" />
              <Column field="reporter" header="REPORTER" />
              <Column body={priorityBodyTemplate} header="PRIORITY" />
              <Column body={statusBodyTemplate} header="STATUS" />
              <Column field="amount" header="AMOUNT" />
              <Column body={actionBodyTemplate} header="" style={{ width: '100px' }} />
            </DataTable>
          </Card>

          {/* Claims Trend Chart */}
          <Card title="Claims Trend Analysis" className="chart-card">
            <Chart type="line" data={claimsTrendData} options={{
              maintainAspectRatio: false,
              responsive: true,
              plugins: {
                legend: {
                  position: 'bottom'
                }
              }
            }} style={{ height: '300px' }} />
          </Card>
        </div>

        <div className="content-right">
          {/* Claims by State */}
          <Card title="Claims by State" className="state-card">
            {claimsByState.map((state, index) => (
              <div key={index} className="state-item">
                <div className="state-info">
                  <span className="state-name">{state.state}</span>
                  <span className="state-value">{state.value} claims</span>
                </div>
                <ProgressBar value={state.percentage} showValue={false} />
              </div>
            ))}
          </Card>

          {/* Claims by Source */}
          <Card title="Claims by Source" className="source-card">
            <Chart type="doughnut" data={claimsBySourceData} options={{
              maintainAspectRatio: false,
              responsive: true,
              plugins: {
                legend: {
                  position: 'bottom'
                }
              }
            }} style={{ height: '250px' }} />
          </Card>

          {/* Loss Ratio */}
          <Card title="Loss Ratio by Product" className="loss-ratio-card">
            <Chart type="bar" data={lossRatioData} options={{
              maintainAspectRatio: false,
              responsive: true,
              plugins: {
                legend: {
                  display: false
                }
              },
              scales: {
                y: {
                  beginAtZero: true,
                  max: 100
                }
              }
            }} style={{ height: '200px' }} />
          </Card>
        </div>
      </div>
    </div>
  );
};

export default ClaimsDashboard;